/* eslint-disable @typescript-eslint/no-explicit-any, max-classes-per-file */
// import { useRouter, RouteLocationRaw } from 'vue-router';
import { RouteLocationRaw } from 'vue-router';
import router from '@/router';

import axios, { isAxiosError } from 'axios';

import { useToastStore } from '@stores/toast-store';
import {
  ApiRequestErrorHandlingOptionsType,
  ErrorType,
  ErrorResponseType,
  ErrorHandlerArgsType,
} from '@api/v1/types';
import { response, errorResponse } from '@api/v1/response';

export const isCancelledError = (error: any): boolean => axios.isCancel(error);

export const isErrorResponse = (error: unknown): error is ErrorResponseType => (
  isAxiosError(error)
  && error.response !== undefined
);

export class BaseErrorHandler {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static handle({ error, options = {} }): void {
    throw new Error('Method \'handle\' must be implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static displayFeedbackMessage(feedbackMessage: string): void {
    throw new Error('Method \'feedbackMessage\' must be implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static logError(error: ErrorType): void {
    throw new Error('Method \'logError\' must be implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static propagateError(error: ErrorType): void {
    throw new Error('Method \'propagateError\' must be implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static redirect(to: RouteLocationRaw): void {
    throw new Error('Method \'redirect\' must be implemented.');
  }
}

export class DefaultErrorHandler extends BaseErrorHandler {
  static handle({ error, options = {} }: ErrorHandlerArgsType): void {
    const {
      disableConsole = false,
      feedbackMessage = '',
      propagateError = false,
      redirectTo = null,
    } = options;

    DefaultErrorHandler.displayFeedbackMessage(feedbackMessage);
    if (!disableConsole) DefaultErrorHandler.logError(error);
    if (propagateError) DefaultErrorHandler.propagateError(error);
    if (redirectTo) DefaultErrorHandler.redirect(redirectTo);
  }

  static displayFeedbackMessage(feedbackMessage: string): void {
    if (feedbackMessage.length === 0) return;
    const toastStore = useToastStore();
    toastStore.warn(feedbackMessage);
  }

  static logError(error: ErrorType): void {
    console.error(error);
  }

  static propagateError(error: ErrorType): void {
    throw error;
  }

  static redirect(to: RouteLocationRaw): void {
    // const router = useRouter();
    router.push(to);
  }
}

export const errorHandlers = Object.freeze({
  default: DefaultErrorHandler,
});

export type ErrorHandlerConstructorType = {
  new (): BaseErrorHandler;
  handle: (args: ErrorHandlerArgsType) => void;
  displayFeedbackMessage: (feedbackMessage: string) => void;
  logError: (error: ErrorType) => void;
  propagateError: (error: ErrorType) => void;
  redirect: (to: RouteLocationRaw) => void;
};

export type WithTryCatchArgsType = {
  impl: () => any;
} & ApiRequestErrorHandlingOptionsType & {
  handler?: null | ErrorHandlerConstructorType;
};

export const withTryCatch = async ({
  impl,
  errorConfig = {
    handler: null,
    options: {},
    useDefault: false,
  },
  callbacks = {
    before: () => {},
    after: () => {},
    onError: () => {},
    finalize: () => {},
  },
}: WithTryCatchArgsType) => {
  const {
    handler = null,
    options = {},
    useDefault = false,
  } = errorConfig;
  const {
    before = () => {},
    after = () => {},
    onError = () => {},
    finalize = () => {},
  } = callbacks;

  const errorHandler = useDefault ? DefaultErrorHandler : handler;

  if (!errorHandler) {
    const res = await impl();
    return res;
  }

  try {
    if (before) await before();
    const res = await impl();
    if (after) await after();
    return response(res);
  } catch (error) {
    if (onError) onError();
    if (errorHandler.handle) {
      errorHandler.handle({ error, options });
    }
    return errorResponse(error);
  } finally {
    if (finalize) finalize();
  }
};
